"use client"

import React, { useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";

export function useColorScheme() {

  // Hook for localStorage - was sessionStorage, which reset this choice on
  // every new tab/browser session; the whole point of remembering it is for
  // it to actually carry over to "next time."
  const useSessionStorage = (keyName, defaultValue) => {
    const [storedValue, setStoredValue] = React.useState(() => {
      if (typeof window === "undefined") return defaultValue;
      try {
        const value = window.localStorage.getItem(keyName);
        if (value !== null) return JSON.parse(value);
        window.localStorage.setItem(keyName, JSON.stringify(defaultValue));
        return defaultValue;
      } catch (err) {
        return defaultValue;
      }
    });

    const setValue = newValue => {
      try {
        if (typeof window !== "undefined") {
          window.localStorage.setItem(keyName, JSON.stringify(newValue));
        }
      } catch (err) {}
      setStoredValue(newValue);
    };

    return [storedValue, setValue];
  };

  // Detect system dark mode - no longer used as the default (see isDark
  // below), kept only in case some other consumer of this hook wants it.
  const systemPrefersDark = useMediaQuery({
    query: "(prefers-color-scheme: dark)"
  });

  // Stored user preference
  const [storedDark, setStoredDark] = useSessionStorage("colorScheme", undefined);

  // Determine final dark mode value. Chart-specific: throughout TVChart's
  // usage of this value (theme:, applyOverrides, the chart-properties
  // color overrides), isDark===true is what actually renders the LIGHT
  // theme - confirmed consistent across every call site, including the
  // real theme-toggle button's own icon-color logic, so it's a confusing
  // name rather than an inversion bug. Defaulting that to
  // systemPrefersDark meant anyone NOT running their OS/browser in dark
  // mode (the common case) got a dark chart by default with no obvious way
  // to know why. Defaulting to true instead makes light the actual default
  // for every first-time visitor, regardless of system preference - the
  // toggle button remains the way to switch to dark and have it remembered.
  const isDark = useMemo(
    () => storedDark !== undefined ? storedDark : true,
    [storedDark]
  );

  // Apply dark mode to body (or data-theme attribute)
  useEffect(() => {
    if (isDark) {
      document.body.classList.add("dark");
      document.body.setAttribute("data-theme", "dark");
    } else {
      document.body.classList.remove("dark");
      document.body.removeAttribute("data-theme");
    }
  }, [isDark]);

  return {
    isDark,
    setIsDark: setStoredDark,
  };
}
